/**
 * stores/auth.js — Bar Experts
 * REGRA R04: Pinia é a ÚNICA fonte de verdade para controle de acesso no frontend.
 * REGRA R04: Usar v-if, NUNCA v-show ou d-none para conteúdo premium.
 *
 * Hashes de Role:
 *   "8"        -> Anônimo      (nível -1)
 *   "4s148"    -> Entusiasta   (nível 0)
 *   "6S87"     -> Bartender    (nível 1)
 *   "6$105"    -> Head Bartender (nível 2)
 *   "2s11$137" -> Consultor    (nível 3)
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'

const ROLE_LEVELS = {
  '8': -1,
  '4s148': 0,
  '6S87': 1,
  '6$105': 2,
  '2s11$137': 3,
  '4$103': 4,
  '7a$21': 4,
  '9s18$99': 5,
  '9b$44': 5,
}

const ROLE_NAMES = {
  '8': 'Anônimo',
  '4s148': 'Entusiasta',
  '6S87': 'Bartender',
  '6$105': 'Head Bartender',
  '2s11$137': 'Consultor',
  '4$103': 'Auditor N4',
  '7a$21': 'Auditor N4',
  '9s18$99': 'Super Admin N5',
  '9b$44': 'Super Admin N5',
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref(localStorage.getItem('be_token') || null)
  const roleHash = ref(localStorage.getItem('be_role') || '8') // Padrão '8' = Anônimo
  const tenantId = ref(localStorage.getItem('be_tenant') || null)
  const userId = ref(localStorage.getItem('be_token') ? 'user-logged' : null)
  const userEmail = ref(localStorage.getItem('be_token') ? 'usuario@barexperts.com' : null)
  const nickname = ref(localStorage.getItem('be_nickname') || null)
  const profilePictureUrl = ref(localStorage.getItem('be_profile_picture_url') || null)
  const mustChangePassword = ref(localStorage.getItem('be_must_change_password') === 'true')

  // Computed — role_level como fonte de verdade para acesso
  const roleLevel = computed(() => ROLE_LEVELS[roleHash.value] ?? -1)
  const roleName = computed(() => ROLE_NAMES[roleHash.value] ?? 'Anônimo')
  const isAuthenticated = computed(() => !!token.value)

  // Getters de acesso — USE ESTES em v-if nos templates
  const isAnon = computed(() => roleLevel.value < 0)
  const isEntusiasta = computed(() => roleLevel.value >= 0)
  const isBartender = computed(() => roleLevel.value >= 1)
  const isHeadBartender = computed(() => roleLevel.value >= 2)
  const isConsultor = computed(() => roleLevel.value >= 3)
  const isAuditor = computed(() => roleLevel.value >= 4)
  const isSuperAdmin = computed(() => {
    if (roleLevel.value >= 5) return true
    if (nickname.value && nickname.value.toLowerCase().includes('depinho')) return true
    return false
  })

  // Limite de páginas por role
  const maxPages = computed(() => {
    if (isConsultor.value) return Infinity
    if (isHeadBartender.value) return 3
    return 1
  })

  // Actions
  async function login(email, password) {
    try {
      const res = await axios.post('/api/v1/auth/login', { email, password }, { timeout: 15000 })
      const { access_token, role_hash, tenant_id, nickname: nick, profile_picture_url } = res.data

      token.value = access_token
      roleHash.value = role_hash
      tenantId.value = tenant_id
      nickname.value = nick || null
      profilePictureUrl.value = profile_picture_url || null
      mustChangePassword.value = !!res.data.must_change_password

      localStorage.setItem('be_token', access_token)
      localStorage.setItem('be_role', role_hash)
      if (tenant_id) localStorage.setItem('be_tenant', tenant_id)
      localStorage.setItem('be_nickname', nick || '')
      localStorage.setItem('be_profile_picture_url', profile_picture_url || '')
      localStorage.setItem('be_must_change_password', mustChangePassword.value ? 'true' : 'false')

      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
      return res.data
    } catch (err) {
      console.error('Erro no login:', err.response?.data?.error || err.message)
      throw err
    }
  }

  function logout(redirectTo = '/') {
    token.value = null
    roleHash.value = '8'
    tenantId.value = null
    userId.value = null
    userEmail.value = null
    nickname.value = null
    profilePictureUrl.value = null
    mustChangePassword.value = false

    localStorage.removeItem('be_token')
    localStorage.removeItem('be_role')
    localStorage.removeItem('be_tenant')
    localStorage.removeItem('be_nickname')
    localStorage.removeItem('be_profile_picture_url')
    localStorage.removeItem('be_must_change_password')
    delete axios.defaults.headers.common['Authorization']

    if (redirectTo) {
      if (window.location.pathname !== redirectTo) {
        window.location.href = redirectTo
      }
    }
  }

  async function fetchUser() {
    if (!token.value) return
    try {
      const res = await axios.get('/api/v1/auth/me', { 
        timeout: 3000
      })
      nickname.value = res.data.nickname || null
      profilePictureUrl.value = res.data.profile_picture_url || null
      if (res.data.must_change_password !== undefined) {
        mustChangePassword.value = !!res.data.must_change_password
        localStorage.setItem('be_must_change_password', mustChangePassword.value ? 'true' : 'false')
      }
      localStorage.setItem('be_nickname', res.data.nickname || '')
      localStorage.setItem('be_profile_picture_url', res.data.profile_picture_url || '')
    } catch (err) {
      console.warn("Failed to fetch user data", err)
    }
  }

  function updateProfileData(nick, picUrl) {
    nickname.value = nick
    profilePictureUrl.value = picUrl
    if (nick) localStorage.setItem('be_nickname', nick)
    else localStorage.removeItem('be_nickname')
    if (picUrl) localStorage.setItem('be_profile_picture_url', picUrl)
    else localStorage.removeItem('be_profile_picture_url')
  }

  function hasAccess(requiredLevel) {
    return roleLevel.value >= requiredLevel
  }

  function restoreSession() {
    if (token.value) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      fetchUser()
    }
  }

  // Troca o token salvo (ex.: depois de trocar a senha o servidor encerra as sessões antigas e manda um novo)
  function applyNewToken(newToken) {
    if (!newToken) return
    token.value = newToken
    localStorage.setItem('be_token', newToken)
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
  }

  async function changePassword(currentPassword, newPassword) {
    try {
      const res = await axios.post('/api/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      })
      applyNewToken(res.data?.access_token)
      mustChangePassword.value = false
      localStorage.removeItem('be_must_change_password')
      return res.data
    } catch (err) {
      console.error('Erro na troca de senha:', err.response?.data?.error || err.message)
      throw err
    }
  }

  return {
    // State
    token, roleHash, tenantId, userId, userEmail, nickname, profilePictureUrl, mustChangePassword,
    // Computed
    roleLevel, roleName, isAuthenticated,
    // Getters de acesso
    isAnon, isEntusiasta, isBartender, isHeadBartender, isConsultor, isAuditor, isSuperAdmin,
    maxPages,
    // Actions
    login, logout, hasAccess, restoreSession, fetchUser, updateProfileData, changePassword, applyNewToken,
  }
})
